import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { GetSearchResultsResponse } from '@shared/models/get-search-results-response';
import { ProductType } from '@shared/models/product-type';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { EntertainmentSearchService } from '@shared/services/search/entertainment-search.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { OnboardingStep } from '@shared/shared/enums/onboarding-step';
import * as EntertainmentSearchStore from '@shared/store/entertainment';
import * as MemberProfileStore from '@shared/store/member-profile';
import * as UserStore from '@shared/store/user';
import { StepChangeParams, TourState } from 'ngx-ui-tour-core';
import { TourService } from 'ngx-ui-tour-md-menu';
import { IMdStepOption } from 'ngx-ui-tour-md-menu/lib/step-option.interface';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, skip, switchMap, take } from 'rxjs/operators';
import { SiteTourAnchors } from '../models/site-tour.enum';

interface TenTourStep extends IMdStepOption {
  asyncAnchorId?: SiteTourAnchors;
  marginTop?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SiteTourService implements OnDestroy {
  private popupFocused: boolean;
  private hasTourBeenRun = false;
  private interval: number;
  public user_authenticated$: Observable<boolean> = this.ngrxStore.select(UserStore.getUserAuthenticated);
  public tourSkipped = false;

  constructor(
    private tourService: TourService,
    private customVariablesService: CustomisableVariablesService,
    private siteSettingsService: SiteSettingsService,
    private entertainmentSearchService: EntertainmentSearchService,
    private ngrxStore: Store<
      UserStore.UserState | MemberProfileStore.MemberProfileState | EntertainmentSearchStore.EntertainmentSearchState
    >
  ) {
    this.tourService.start$.subscribe(() => {
      this.hasTourBeenRun = true;
      window.addEventListener('resize', this.resizeEvent.bind(this));
    });

    this.tourService.stepShow$.subscribe((stepOption: StepChangeParams<TenTourStep>) => {
      this.popupFocused = false;
      const pageHeight = window.document.body.clientHeight;

      // Hide the popup until the async element is rendered
      if (stepOption.step && stepOption.step.asyncAnchorId) {
        this.setPopupVisibility(false);
      }

      if (stepOption.step && stepOption.step.asyncAnchorId === SiteTourAnchors.PreferencesFollow) {
        this.ngrxStore
          .select(EntertainmentSearchStore.getEntertainmentSearchState)
          .pipe(skip(1), take(1))
          .subscribe((data) => {
            if (!data.results.items.length || !data.results.hasFollowAnchor) {
              this.tourService.end();
              clearInterval(this.interval);
            }
          });
      }

      if (this.interval) {
        clearInterval(this.interval);
      }

      this.interval = window.setInterval(() => {
        if (pageHeight !== window.document.body.clientHeight) {
          const anchorRect = this.getAnchorElementRect(stepOption.step);
          const isAsyncAnchorRendered = !!document.getElementById(stepOption.step.asyncAnchorId);

          if (anchorRect && stepOption.step.asyncAnchorId && isAsyncAnchorRendered) {
            this.setPopupVisibility(true);
            const anchorPopup = document.getElementById('mat-menu-panel-0');
            const newAnchorPopupStyles: Record<string, string> = {
              position: 'fixed',
              top: anchorRect.top + anchorRect.height + 'px',
            };

            // Check if popup fits the screen when aligned to the left of the anchor
            if (anchorRect.left < window.outerWidth - anchorPopup.offsetWidth) {
              newAnchorPopupStyles.left = `${anchorRect.left}px`;
              newAnchorPopupStyles.right = 'unset';
              // Otherwise align to the right of the screen
            } else {
              newAnchorPopupStyles.left = 'unset';
              newAnchorPopupStyles.right = '0px';
            }

            Object.assign(anchorPopup.style, newAnchorPopupStyles);
            this.focusPopup();
          }

          this.scrollIntoPosition(stepOption.step);
          this.setBackdropPosition(anchorRect);
        }
      }, 250);

      window.setTimeout(() => {
        const overlay = document.getElementsByClassName('cdk-overlay-container')[0] as HTMLElement;
        if (overlay) {
          overlay.style.zIndex = '1000';
        }

        this.scrollIntoPosition(stepOption.step);
        this.setBackdropPosition(this.getAnchorElementRect(stepOption.step));

        if (!stepOption.step.asyncAnchorId) {
          this.focusPopup();
        }
      });
    });

    this.tourService.end$.subscribe(() => {
      window.removeEventListener('resize', this.resizeEvent);
      window.document.getElementById('js-backdrop').style.display = 'none';
      document.body.style.overflowY = 'initial';
      if (this.interval) {
        clearInterval(this.interval);
      }
      this.ngrxStore.dispatch(
        new MemberProfileStore.UpdateMemberProfile({
          viewed_tour: this.tourSkipped ? OnboardingStep.STEP_TWO : OnboardingStep.COMPLETED,
        })
      );
    });

    // if the user logs out close the modal
    this.user_authenticated$.subscribe((res) => {
      this.hasTourBeenRun = false;
      if (!res && this.tourService.getStatus() === TourState.ON) {
        this.tourService.end();
        // reset tour so it can be shown to the user again on next log in
      }
    });
  }

  public resizeEvent() {
    const currentStep = this.tourService.currentStep;
    this.setBackdropPosition(this.getAnchorElementRect(currentStep));
  }

  private getAnchorElementRect(currentStep: TenTourStep): DOMRect {
    if (!currentStep) {
      return null;
    }

    const asyncAnchorElement = document.getElementById(currentStep.asyncAnchorId);

    if (asyncAnchorElement) {
      return asyncAnchorElement.getBoundingClientRect();
    } else {
      return document.getElementById(currentStep.anchorId)
        ? document.getElementById(currentStep.anchorId).getBoundingClientRect()
        : null;
    }
  }

  private returnBackdropDom(): HTMLElement {
    return window.document.getElementById('js-backdrop');
  }

  private setBackdropPosition(rect: DOMRect) {
    if (!rect) {
      return;
    }
    const backdrop = this.returnBackdropDom();

    if (backdrop) {
      backdrop.style.display = 'block';
      backdrop.style.width = `${rect.width}px`;
      backdrop.style.top = `${rect.top}px`;
      backdrop.style.left = `${rect.left}px`;
      backdrop.style.height = `${rect.height}px`;
    }
  }

  private scrollIntoPosition(step: TenTourStep) {
    const anchorElement = document.getElementById(step.asyncAnchorId || step.anchorId);
    if (!anchorElement) {
      return;
    }
    anchorElement.scrollIntoView();
    const elementHeight = anchorElement.clientHeight || anchorElement.offsetHeight;
    const scrolledY = window.scrollY;
    if (scrolledY) {
      window.scroll(0, scrolledY - elementHeight - 150);
    }
  }

  public getTourText() {
    combineLatest([this.customVariablesService.variables, this.siteSettingsService.fetchSiteSettings()])
      .pipe(
        filter(([variables, siteSettings]) => !!variables && !!siteSettings),
        switchMap(([variables, siteSettings]) => {
          let entertainmentSearchResults$: Observable<GetSearchResultsResponse>;

          if (siteSettings.switches.preferences_artist) {
            entertainmentSearchResults$ = this.entertainmentSearchService.getEntertainmentResults('', [], 1);
          } else {
            entertainmentSearchResults$ = of(<GetSearchResultsResponse>{ items: [] });
          }

          return combineLatest([of(variables), entertainmentSearchResults$]);
        }),
        take(1)
      )
      .subscribe(([variables, entertainmentSearchResults]) => {
        const isFollowButtonVisible = entertainmentSearchResults.items?.some(
          (item) => item.followButton && item.followButton.product.type === ProductType.Artist
        );

        const tourInformation: TenTourStep[] = [
          {
            title: variables.landing_page_burger_menu_title,
            content: variables.spotlight_burgermenu_icon,
            anchorId: SiteTourAnchors.NavMenu,
            route: '/landing',
          },
          {
            title: variables.landing_page_persona_icon_title,
            content: variables.spotlight_profile_icon,
            anchorId: SiteTourAnchors.NavPerson,
            route: '/landing',
          },
          {
            title: variables.profile_page_change_password_title,
            content: variables.spotlight_profile_details,
            anchorId: SiteTourAnchors.ProfileDetails,
            route: '/member/details',
            marginTop: 15,
          },
          {
            title: variables.profile_page_activities_title,
            content: variables.spotlight_profile_activities,
            anchorId: SiteTourAnchors.ProfileActivities,
            marginTop: 15,
            route: '/member/details',
          },
        ];

        // Show follow artists tour step only if a follow button will be visible on the entertainment page
        if (isFollowButtonVisible) {
          tourInformation.push({
            content: variables.spotlight_follow_artist,
            anchorId: SiteTourAnchors.AsyncPlaceholder,
            asyncAnchorId: SiteTourAnchors.PreferencesFollow,
            marginTop: 15,
            route: '/entertainment/search',
          });
        }

        if (!this.hasTourBeenRun) {
          this.tourService.initialize(tourInformation);

          setTimeout(() => {
            this.tourService.start();
            document.body.style.overflowY = 'hidden';
          }, 1000);
        }
      });
  }

  public start() {
    this.getTourText();
  }

  public ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  private focusPopup() {
    if (this.popupFocused) {
      return;
    }

    const focusTrap = document.getElementById('tour-focus-trap');

    if (focusTrap) {
      focusTrap.focus();
      this.popupFocused = true;
    }
  }

  private setPopupVisibility(isVisible: boolean) {
    const anchorPopup = document.getElementById('mat-menu-panel-0');
    anchorPopup.style.display = isVisible ? 'block' : 'none';
  }
}
